import React, { useState, useCallback } from 'react';
import { CloudArrowUpIcon, DocumentTextIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const BigQueryConnectionForm = ({ details, onChange, errors = {}, readOnly = false }) => {
  const [dragActive, setDragActive] = useState(false);
  const [jsonError, setJsonError] = useState(null);
  const [jsonValid, setJsonValid] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange({
      ...details,
      [name]: value
    });
  };

  // Parse and validate service account JSON
  const parseServiceAccountJson = useCallback((jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);

      // Validate required fields
      const requiredFields = ['type', 'project_id', 'private_key', 'client_email'];
      const missingFields = requiredFields.filter(field => !parsed[field]);

      if (missingFields.length > 0) {
        setJsonError(`Missing required fields: ${missingFields.join(', ')}`);
        setJsonValid(false);
        return null;
      }

      if (parsed.type !== 'service_account') {
        setJsonError('Invalid JSON: type must be "service_account"');
        setJsonValid(false);
        return null;
      }

      setJsonError(null);
      setJsonValid(true);
      return parsed;
    } catch (e) {
      setJsonError('Invalid JSON format');
      setJsonValid(false);
      return null;
    }
  }, []);

  // Handle JSON text input
  const handleJsonChange = (e) => {
    const jsonString = e.target.value;
    onChange({
      ...details,
      service_account_json: jsonString
    });

    if (jsonString.trim()) {
      const parsed = parseServiceAccountJson(jsonString);
      if (parsed) {
        // Auto-fill project ID from service account
        onChange({
          ...details,
          service_account_json: jsonString,
          project_id: parsed.project_id
        });
      }
    } else {
      setJsonError(null);
      setJsonValid(false);
    }
  };

  // Handle file drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (readOnly) return;

    const files = e.dataTransfer?.files;
    if (files && files[0]) {
      const file = files[0];
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const jsonString = event.target.result;
          const parsed = parseServiceAccountJson(jsonString);
          if (parsed) {
            onChange({
              ...details,
              service_account_json: jsonString,
              project_id: parsed.project_id
            });
          } else {
            onChange({
              ...details,
              service_account_json: jsonString
            });
          }
        };
        reader.readAsText(file);
      } else {
        setJsonError('Please upload a JSON file');
      }
    }
  }, [details, onChange, parseServiceAccountJson, readOnly]);

  // Handle file input
  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const jsonString = event.target.result;
        const parsed = parseServiceAccountJson(jsonString);
        if (parsed) {
          onChange({
            ...details,
            service_account_json: jsonString,
            project_id: parsed.project_id
          });
        } else {
          onChange({
            ...details,
            service_account_json: jsonString
          });
        }
      };
      reader.readAsText(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!readOnly) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  // Location options
  const locations = [
    { value: 'US', label: 'United States (multi-region)' },
    { value: 'EU', label: 'European Union (multi-region)' },
    { value: 'us-central1', label: 'Iowa (us-central1)' },
    { value: 'us-east1', label: 'South Carolina (us-east1)' },
    { value: 'us-east4', label: 'Northern Virginia (us-east4)' },
    { value: 'us-west1', label: 'Oregon (us-west1)' },
    { value: 'us-west2', label: 'Los Angeles (us-west2)' },
    { value: 'europe-west1', label: 'Belgium (europe-west1)' },
    { value: 'europe-west2', label: 'London (europe-west2)' },
    { value: 'asia-east1', label: 'Taiwan (asia-east1)' },
    { value: 'asia-northeast1', label: 'Tokyo (asia-northeast1)' },
    { value: 'australia-southeast1', label: 'Sydney (australia-southeast1)' },
  ];

  return (
    <div className="space-y-6">
      {/* Service Account JSON Upload */}
      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          Service Account JSON Key
        </label>
        <p className="text-sm text-secondary-500 mb-3">
          Upload or paste your Google Cloud service account JSON key file.
          <a
            href="https://cloud.google.com/iam/docs/keys-create-delete"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-1 text-primary-600 hover:text-primary-700"
          >
            Learn how to create one
          </a>
        </p>

        {!readOnly && (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-secondary-300 hover:border-secondary-400'
            }`}
          >
            <input
              type="file"
              accept=".json,application/json"
              onChange={handleFileInput}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <CloudArrowUpIcon className="mx-auto h-12 w-12 text-secondary-400" />
            <p className="mt-2 text-sm text-secondary-600">
              <span className="font-medium text-primary-600">Click to upload</span> or drag and drop
            </p>
            <p className="mt-1 text-xs text-secondary-500">JSON file only</p>
          </div>
        )}

        {/* JSON Text Area */}
        <div className="mt-4">
          <label htmlFor="service_account_json" className="block text-sm font-medium text-secondary-700 mb-1">
            Or paste JSON directly
          </label>
          <textarea
            id="service_account_json"
            name="service_account_json"
            rows={6}
            value={details.service_account_json || ''}
            onChange={handleJsonChange}
            readOnly={readOnly}
            className={`mt-1 block w-full shadow-sm sm:text-sm border-secondary-300 rounded-md font-mono text-xs ${
              readOnly
                ? 'bg-gray-50 text-gray-900 cursor-default focus:ring-0 focus:border-secondary-300'
                : 'focus:ring-primary-500 focus:border-primary-500'
            } ${errors.service_account_json || jsonError ? 'border-danger-300' : ''}`}
            placeholder={readOnly ? '' : '{\n  "type": "service_account",\n  "project_id": "your-project-id",\n  ...\n}'}
          />
        </div>

        {/* Validation Status */}
        {(jsonError || jsonValid) && (
          <div className={`mt-2 flex items-center text-sm ${jsonError ? 'text-danger-600' : 'text-accent-600'}`}>
            {jsonError ? (
              <>
                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                {jsonError}
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                Valid service account JSON
              </>
            )}
          </div>
        )}

        {errors.service_account_json && (
          <p className="mt-2 text-sm text-danger-600">{errors.service_account_json}</p>
        )}
      </div>

      {/* Project and Dataset Configuration */}
      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
        <div className="sm:col-span-3">
          <label htmlFor="project_id" className="block text-sm font-medium text-secondary-700">
            Project ID
          </label>
          <div className="mt-1 relative">
            <input
              type="text"
              name="project_id"
              id="project_id"
              value={details.project_id || ''}
              onChange={handleChange}
              readOnly={readOnly}
              className={`block w-full shadow-sm sm:text-sm border-secondary-300 rounded-md ${
                readOnly
                  ? 'bg-gray-50 text-gray-900 cursor-default focus:ring-0 focus:border-secondary-300'
                  : 'focus:ring-primary-500 focus:border-primary-500'
              } ${errors.project_id ? 'border-danger-300' : ''}`}
              placeholder="my-gcp-project"
            />
            {jsonValid && details.project_id && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <CheckCircleIcon className="h-5 w-5 text-accent-500" />
              </div>
            )}
          </div>
          {errors.project_id && (
            <p className="mt-2 text-sm text-danger-600">{errors.project_id}</p>
          )}
          <p className="mt-1 text-xs text-secondary-500">
            Auto-filled from service account JSON
          </p>
        </div>

        <div className="sm:col-span-3">
          <label htmlFor="dataset" className="block text-sm font-medium text-secondary-700">
            Default Dataset
          </label>
          <input
            type="text"
            name="dataset"
            id="dataset"
            value={details.dataset || ''}
            onChange={handleChange}
            readOnly={readOnly}
            className={`mt-1 block w-full shadow-sm sm:text-sm border-secondary-300 rounded-md ${
              readOnly
                ? 'bg-gray-50 text-gray-900 cursor-default focus:ring-0 focus:border-secondary-300'
                : 'focus:ring-primary-500 focus:border-primary-500'
            } ${errors.dataset ? 'border-danger-300' : ''}`}
            placeholder="my_dataset"
          />
          {errors.dataset && (
            <p className="mt-2 text-sm text-danger-600">{errors.dataset}</p>
          )}
          <p className="mt-1 text-xs text-secondary-500">
            Optional. You can select datasets after connecting.
          </p>
        </div>

        <div className="sm:col-span-3">
          <label htmlFor="location" className="block text-sm font-medium text-secondary-700">
            Location
          </label>
          <select
            id="location"
            name="location"
            value={details.location || 'US'}
            onChange={handleChange}
            disabled={readOnly}
            className={`mt-1 block w-full py-2 px-3 border border-secondary-300 bg-white rounded-md shadow-sm sm:text-sm ${
              readOnly
                ? 'bg-gray-50 text-gray-900 cursor-default'
                : 'focus:outline-none focus:ring-primary-500 focus:border-primary-500'
            }`}
          >
            {locations.map((loc) => (
              <option key={loc.value} value={loc.value}>
                {loc.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-secondary-500">
            Must match your dataset location
          </p>
        </div>
      </div>

      {/* Info Box */}
      <div className="rounded-md bg-blue-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <DocumentTextIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Required Permissions</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>Your service account needs these roles:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li><code className="text-xs bg-blue-100 px-1 rounded">BigQuery Data Viewer</code> - Read table data</li>
                <li><code className="text-xs bg-blue-100 px-1 rounded">BigQuery Metadata Viewer</code> - Read schema information</li>
                <li><code className="text-xs bg-blue-100 px-1 rounded">BigQuery Job User</code> - Run queries</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BigQueryConnectionForm;
